import { AnimatedSprite, Assets, Container, Spritesheet, Texture } from "pixi.js";

import { SEALIFE } from "../../../shared/Config";
import { shortestAngle } from "../../../shared/Sailing";
import { SEALIFE_SPECIES, SeaLifeSettings, SeaLifeSpecies, seaLifeSpecies } from "../../../shared/Settings";
import { SEA_COLOR } from "./Water";

/** The spritesheet, by its manifest alias. */
const SHEET = "SeaLife";

/**
 * One species' dials, ready to use: pairs put the right way round and the turn rate in radians.
 *
 * Resolving them once here is what keeps the per-frame code free of `Math.min` on a pair of
 * sliders. The min/max pairs are sorted rather than trusted because fields are coerced one at a
 * time, so a dashboard where a shark's smallest is bigger than its largest is a storable state.
 */
interface Dials {
    /** How many of this animal the pool holds. */
    count: number;
    sizeSmall: number;
    sizeLarge: number;
    speedSlow: number;
    speedFast: number;
    /** Radians per second. */
    turnRate: number;
    /** Frames per second of the swim cycle. */
    fps: number;
}

function resolve(values: SeaLifeSettings): Record<SeaLifeSpecies, Dials> {
    const out = {} as Record<SeaLifeSpecies, Dials>;

    for (const species of SEALIFE_SPECIES) {
        const dials = seaLifeSpecies(values, species);
        out[species] = {
            count: Math.max(0, Math.round(dials.count)),
            sizeSmall: Math.min(dials.sizeMin, dials.sizeMax),
            sizeLarge: Math.max(dials.sizeMin, dials.sizeMax),
            speedSlow: Math.min(dials.speedMin, dials.speedMax),
            speedFast: Math.max(dials.speedMin, dials.speedMax),
            turnRate: (dials.turnRate * Math.PI) / 180,
            fps: dials.animationFps
        };
    }

    return out;
}

/** The dials, read once at module load like every other setting — see design/Settings.md. */
let DIALS = resolve(SEALIFE);
let OPACITY = SEALIFE.opacity;
let DEPTH = SEALIFE.depth;

/**
 * Puts new numbers behind the dials above.
 *
 * **For the dashboard's workspace and nothing else**, exactly as `setWaveDials` is — see the note
 * there. The school is decoration nobody else can see, so there is even less to disagree with
 * here than there is over the water. Turn rate and swim cadence are read per frame, so they land
 * immediately; a species' sizes and speeds land as its animals recycle; `restock` is what applies
 * a new population, opacity or depth at once.
 */
export function setSeaLifeDials(values: SeaLifeSettings): void {
    DIALS = resolve(values);
    OPACITY = values.opacity;
    DEPTH = values.depth;
}

/** How long an animal holds a course before drifting onto another one, in seconds. */
const COURSE_HOLD_MIN = 3;
const COURSE_HOLD_MAX = 9;
/**
 * Largest course change an animal picks at once, in radians. Small on purpose: an animal
 * that swings hard reads as one that has been startled, and nothing down there is being
 * chased. What it means for a crossing is that the path bends rather than turns.
 */
const COURSE_DRIFT_MAX = Math.PI * 0.22;
/**
 * How far off a straight run inwards a fresh animal may enter, in radians. Without it every
 * animal crosses square to the edge it came in on and the school reads as a conveyor belt.
 */
const ENTRY_SPREAD = Math.PI * 0.28;
/** Extra distance past its own radius before an animal is recycled, in pixels. */
const EXIT_MARGIN = 60;

/**
 * One species: what it is called in the settings, and which animation plays.
 *
 * This is all that is left of what used to be a table of **factors** — a length multiple and a
 * cadence multiple per animal, there to make a shark the biggest thing in the water and a ray the
 * slowest flapper out of one shared size dial and one shared cadence dial. Every one of those
 * dials is now the animal's own, so the factors had nothing left to do and their effect lives in
 * the defaults in `shared/Settings.ts` instead. See design/SeaLife.md.
 *
 * What is left is the art, and that stays here: which frames a hammerhead plays is a fact about
 * the sheet, and the sheet is the renderer's business.
 */
export interface Species {
    /** Which animal this is. The key its dials are stored under in `SeaLifeSettings`. */
    key: SeaLifeSpecies;
    /** Animation name in the `SeaLife` sheet. */
    animation: string;
}

/**
 * The roster, in the order `SEALIFE_SPECIES` lists it.
 *
 * Exported for the dashboard's Sea life panel, which draws a tile per species and needs the art
 * name to put a picture on it — the sheet decides what a hammerhead looks like, so nothing else
 * should be repeating the frame name. Nothing in the game reads it from outside this file.
 */
export const SPECIES: Species[] = [
    { key: "fish", animation: "sealife/fish/swim" },
    { key: "hammer", animation: "sealife/hammer/swim" },
    { key: "ray", animation: "sealife/ray/swim" },
    { key: "shark", animation: "sealife/shark/swim" },
    { key: "turtle", animation: "sealife/turtle/swim" }
];

/** One animal in the water. Screen-space, like everything else that rides the viewport. */
interface Animal {
    sprite: AnimatedSprite;
    /**
     * What this animal is, for the life of the sprite. A recycled animal comes back as itself
     * rather than being re-rolled into another species: the populations are exact, so the
     * hammerhead that swims out of shot is the hammerhead that swims back in.
     */
    species: Species;
    /** Position in viewport pixels — the underwater layer's origin is the top-left corner. */
    x: number;
    y: number;
    /** Heading in radians, 0 = north, growing clockwise. Same convention as a hull. */
    heading: number;
    targetHeading: number;
    /** Pixels per second along `heading`. */
    speed: number;
    /** Where in its swim cycle it is, in seconds. Stepped at its species' cadence. */
    frameTime: number;
    /** Seconds left on the current course. */
    holdTimer: number;
    /** Half the on-screen diagonal, in pixels. What entering and leaving are measured by. */
    radius: number;
}

/** Linear blend between two packed RGB colours. `amount` 0 is `from`, 1 is `to`. */
function mixColor(from: number, to: number, amount: number): number {
    const blend = (shift: number) => {
        const a = (from >> shift) & 0xff;
        const b = (to >> shift) & 0xff;
        return Math.round(a + (b - a) * amount) << shift;
    };
    return blend(16) | blend(8) | blend(0);
}

/**
 * The school: fish, hammerheads, rays, sharks and turtles crossing under the water.
 *
 * **Decoration only.** It is client-side, unsynchronised and untouchable — the server does
 * not know it exists, no boat can collide with it, and two players in the same room see
 * different animals. See design/SeaLife.md.
 *
 * It lives in `Water.underwater`, which puts it under the wave overlay and inside the
 * displacement filter: the swell warps the animals along with the water, which is the whole
 * reason they read as submerged rather than as sprites laid over a sea. This is the case
 * design/Water.md's first rule is about — loose in the sea, so it belongs to the swell.
 *
 * The school is a **fixed pool**, stocked from the five species populations: exactly
 * `sharkCount` sharks are made once and recycled forever. An animal that swims out of shot is
 * re-rolled — size, speed and swim rate, but never its species — and put back in at a random
 * edge, so the water is never briefly empty, the mix on screen is exactly what the dashboard
 * says, and nothing is ever allocated mid-battle.
 */
export class SeaLife {
    private readonly _container = new Container();
    private readonly _animals: Animal[] = [];

    private _width: number;
    private _height: number;

    public get container(): Container {
        return this._container;
    }

    constructor(width: number, height: number) {
        this._width = width;
        this._height = height;

        this.restock();
    }

    /**
     * Brings the pool to the stored population **species by species**, and re-applies the opacity
     * and the depth to every animal already in it.
     *
     * Only the dashboard calls it — see `setSeaLifeDials`. In a battle the school is stocked once
     * in the constructor and recycled forever. Growing keeps the animals already swimming, so
     * nudging a species up adds to the water rather than emptying it and starting again, and
     * nudging one down takes only that animal out.
     */
    public restock(): void {
        for (const species of SPECIES) {
            const wanted = DIALS[species.key].count;

            // Counted, then trimmed from the back: `surplus` falls to zero as the extras go, so
            // whatever is left of it negative is the shortfall the second loop makes up.
            let surplus = this._tally(species) - wanted;
            for (let index = this._animals.length - 1; index >= 0 && surplus > 0; index--) {
                if (this._animals[index].species !== species) continue;
                this._animals[index].sprite.destroy();
                this._animals.splice(index, 1);
                surplus--;
            }

            for (let short = -surplus; short > 0; short--) this._animals.push(this._stock(species));
        }

        for (const animal of this._animals) {
            animal.sprite.alpha = OPACITY;
            animal.sprite.tint = mixColor(0xffffff, SEA_COLOR, DEPTH);
        }
    }

    /** How many of one species are in the water right now. */
    private _tally(species: Species): number {
        let count = 0;
        for (const animal of this._animals) if (animal.species === species) count++;
        return count;
    }

    /** One animal of a species, made and put in the water somewhere in shot. */
    private _stock(species: Species): Animal {
        const sprite = new AnimatedSprite({
            // Driven by hand rather than by autoUpdate, so the cycle runs off the game's clock
            // in seconds instead of a second, free-running Ticker.shared.
            textures: this._frames(species),
            anchor: 0.5,
            alpha: OPACITY,
            tint: mixColor(0xffffff, SEA_COLOR, DEPTH),
            autoUpdate: false
        });
        this._container.addChild(sprite);

        const animal: Animal = {
            sprite,
            species,
            x: 0,
            y: 0,
            heading: 0,
            targetHeading: 0,
            speed: 0,
            frameTime: 0,
            holdTimer: 0,
            radius: 0
        };

        // The opening school is scattered across the viewport rather than queued up at
        // the edges, or the first few seconds of every scene are empty water.
        this._roll(animal);
        this._place(animal, false);

        return animal;
    }

    public update(deltaSeconds: number): void {
        for (const animal of this._animals) {
            animal.holdTimer -= deltaSeconds;
            if (animal.holdTimer <= 0) this._chooseCourse(animal);

            // Same swing as a hull, at this species' own rate: clamped to what is left of the
            // turn so it settles on a course instead of hunting either side of it. Read off the
            // dials per frame rather than baked in at a roll, so a drag on the slider is felt now.
            const turnRate = DIALS[animal.species.key].turnRate;
            const delta = shortestAngle(animal.heading, animal.targetHeading);
            animal.heading += Math.sign(delta) * Math.min(Math.abs(delta), turnRate * deltaSeconds);

            // Heading 0 is north, so y takes the cosine negated — as for a boat.
            animal.x += Math.sin(animal.heading) * animal.speed * deltaSeconds;
            animal.y -= Math.cos(animal.heading) * animal.speed * deltaSeconds;

            this._advanceSwim(animal, deltaSeconds);

            animal.sprite.position.set(animal.x, animal.y);
            animal.sprite.rotation = animal.heading;

            if (this._hasLeft(animal)) {
                this._roll(animal);
                this._place(animal, true);
            }
        }
    }

    /**
     * Slides the school by a world offset in screen pixels, exactly as `Water.scroll` slides
     * the waves. The animals are *in* the water, so a boat sailing east has to leave them
     * astern — without this they would hang off the camera and swim along with the player.
     */
    public scroll(deltaX: number, deltaY: number): void {
        for (const animal of this._animals) {
            animal.x -= deltaX;
            animal.y -= deltaY;
        }
    }

    public resize(width: number, height: number): void {
        this._width = width;
        this._height = height;
    }

    public destroy(): void {
        this._container.destroy({ children: true });
    }

    /** The frames of one species' swim cycle, from the loaded sheet. */
    private _frames(species: Species): Texture[] {
        return Assets.get<Spritesheet>(SHEET).animations[species.animation];
    }

    /**
     * Re-rolls everything about an animal except what it is and where it is: its size and its
     * speed, out of its own species' range. Called on every recycle, so the same shark comes back
     * a different shark rather than the school being the same five silhouettes going round.
     */
    private _roll(animal: Animal): void {
        const dials = DIALS[animal.species.key];

        // One roll drives both size and speed, and speed reads it backwards: the biggest
        // animal in the water is the slowest and the smallest is the quickest. Rolling them
        // apart gives sprinting turtles, which is funny once and wrong afterwards.
        const roll = Math.random();
        const length = dials.sizeSmall + (dials.sizeLarge - dials.sizeSmall) * roll;
        animal.speed = dials.speedFast + (dials.speedSlow - dials.speedFast) * roll;

        // texture.height is the untrimmed frame height, so a length holds whatever the packer
        // cropped off — the same reason `Boat` scales off it rather than off the drawn size.
        const texture = animal.sprite.texture;
        animal.sprite.scale.set(length / texture.height);
        animal.radius = Math.hypot(texture.width, texture.height) * animal.sprite.scale.x * 0.5;

        // A shared start frame would have the whole school flapping in unison, which reads as
        // one mechanism rather than as several animals.
        animal.frameTime = Math.random() * (animal.sprite.totalFrames / dials.fps);
    }

    /**
     * Puts an animal in the water. `fromEdge` enters it just out of shot on a course that
     * crosses the viewport; otherwise it is dropped anywhere inside on any heading, which is
     * only used to seed the opening school.
     */
    private _place(animal: Animal, fromEdge: boolean): void {
        if (!fromEdge) {
            animal.x = Math.random() * this._width;
            animal.y = Math.random() * this._height;
            animal.heading = Math.random() * Math.PI * 2;
        } else {
            // Just far enough out to be fully hidden, and no further: past the plate's
            // overscan the filter would clip it, and it would arrive with a straight edge.
            const out = animal.radius;
            const inward = Math.floor(Math.random() * 4);

            if (inward === 0) {
                animal.x = Math.random() * this._width;
                animal.y = -out;
                animal.heading = Math.PI; // in from the top, heading south
            } else if (inward === 1) {
                animal.x = Math.random() * this._width;
                animal.y = this._height + out;
                animal.heading = 0; // in from the bottom, heading north
            } else if (inward === 2) {
                animal.x = -out;
                animal.y = Math.random() * this._height;
                animal.heading = Math.PI / 2; // in from the left, heading east
            } else {
                animal.x = this._width + out;
                animal.y = Math.random() * this._height;
                animal.heading = -Math.PI / 2; // in from the right, heading west
            }

            animal.heading += (Math.random() * 2 - 1) * ENTRY_SPREAD;
        }

        animal.targetHeading = animal.heading;
        animal.holdTimer = COURSE_HOLD_MIN + Math.random() * (COURSE_HOLD_MAX - COURSE_HOLD_MIN);
        animal.sprite.position.set(animal.x, animal.y);
        animal.sprite.rotation = animal.heading;
    }

    /** Bends the current course a little, and decides how long to hold the new one. */
    private _chooseCourse(animal: Animal): void {
        animal.targetHeading = animal.heading + (Math.random() * 2 - 1) * COURSE_DRIFT_MAX;
        animal.holdTimer = COURSE_HOLD_MIN + Math.random() * (COURSE_HOLD_MAX - COURSE_HOLD_MIN);
    }

    /**
     * Steps the swim cycle at this species' own cadence, regardless of the render frame rate.
     *
     * Read off the dials per frame rather than baked in at a roll, so dragging a ray's cadence in
     * the dashboard changes how the rays already in the water are flapping. The wrap keeps the
     * accumulator small however long the scene has been up, and keeps the frame in range when the
     * cadence is dropped under a `frameTime` that was rolled against a faster one.
     */
    private _advanceSwim(animal: Animal, deltaSeconds: number): void {
        const fps = DIALS[animal.species.key].fps;
        const loopSeconds = animal.sprite.totalFrames / fps;
        animal.frameTime = (animal.frameTime + deltaSeconds) % loopSeconds;
        animal.sprite.currentFrame = Math.floor(animal.frameTime * fps);
    }

    /** Whether an animal is far enough past an edge that recycling it cannot be seen. */
    private _hasLeft(animal: Animal): boolean {
        const margin = animal.radius + EXIT_MARGIN;
        return animal.x < -margin || animal.x > this._width + margin || animal.y < -margin || animal.y > this._height + margin;
    }
}
